import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** Start of a candidate dismiss swipe: {x, y, t} or null. */
  #swipeStart = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    // Swipe-up-to-dismiss on touch (Jan, 2026-07). Passive listeners on the
    // dialog: they only OBSERVE, so drag-zoom-wrapper's pinch/pan (which
    // preventDefaults its own touchmove) keeps working underneath.
    this.refs.dialog.addEventListener('touchstart', this.#onSwipeStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#onSwipeEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('touchstart', this.#onSwipeStart);
    this.refs.dialog.removeEventListener('touchend', this.#onSwipeEnd);
  }

  /**
   * Begin tracking a possible dismiss swipe. Only a SINGLE finger qualifies — a
   * second touch means a pinch-zoom, which cancels the candidate.
   * @param {TouchEvent} event
   */
  #onSwipeStart = (event) => {
    if (event.touches.length !== 1) {
      this.#swipeStart = null;
      return;
    }
    const touch = event.touches[0];
    this.#swipeStart = { x: touch.clientX, y: touch.clientY, t: performance.now() };
  };

  /**
   * A quick, decisive UPWARD flick dismisses the zoom — a common mobile gesture
   * for closing fullscreen content. Thresholds are deliberately high so a slow
   * pan-drag of the zoomed image (drag-zoom-wrapper, which opens at 1.5× on
   * mobile) does NOT accidentally close: ≥80px upward, mostly vertical, ≤400ms.
   * @param {TouchEvent} event
   */
  #onSwipeEnd = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    // Bail if a pinch was underway, or fingers are still down.
    if (!start || event.touches.length) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const dt = performance.now() - start.t;

    if (dy <= -80 && Math.abs(dy) >= Math.abs(dx) * 2 && dt <= 400) {
      this.close();
    }
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // Flag the open zoom so the fixed sticky add-to-cart bar can hide itself
      // (see product-information.liquid `html.zoom-dialog-open`).
      document.documentElement.classList.add('zoom-dialog-open');

      // Reset each zoom wrapper to the CURRENT breakpoint's initial scale. The
      // wrapper captured its scale in connectedCallback at page-load width, so
      // after a desktop→mobile resize it would open at the stale desktop scale
      // (1×, image tiny/contained with big side+bottom margins) instead of the
      // mobile 1.5× fill (Jan, 2026-07-24 — "make the modal full width/height").
      for (const wrapper of dialog.querySelectorAll('drag-zoom-wrapper')) {
        /** @type {any} */ (wrapper).resetToInitial?.();
      }

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    // Curaprox 2026-07: open the zoom modal instantly, WITHOUT the view-
    // transition morph. The morph gave the clicked image its OWN
    // `gallery-item-open` view-transition group, which finished entering a beat
    // before the dialog panel — so the image appeared first and the white
    // background arrived late ("image appears first, then the background",
    // client, repeatedly). Because the image is a normal DOM child of the
    // dialog, opening straight through `showModal()` makes the image and the
    // panel enter TOGETHER on the same timing, governed by the dialog's own
    // uniform opacity fade (base.css `zoom-dialog dialog`). Closing keeps its
    // view transition (see `close()`), which was never reported as a problem.
    open();
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Click anywhere on a slide. On the photo itself → zoom IN a step; on the
   * white/letterbox area around it → close the modal. The X button and Escape
   * call `close()` directly and are never intercepted.
   * @param {MouseEvent} event
   */
  closeFromImage(event) {
    const target = event?.target instanceof Element ? event.target : null;
    const wrapper = /** @type {any} */ (
      target?.querySelector('drag-zoom-wrapper') ?? target?.closest('drag-zoom-wrapper')
    );

    if (wrapper?.handleImageClick && typeof event.clientX === 'number') {
      const action = wrapper.handleImageClick(event.clientX, event.clientY);
      if (action !== 'outside') return;
    }

    return this.close();
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver. IO delivery is
    // part of the rendering steps and can stall (hidden/occluded tab) — race a
    // timeout so close() can never hang with the dialog stuck open.
    const mostVisibleElement =
      (await Promise.race([
        getMostVisibleElement(media),
        new Promise((resolve) => setTimeout(() => resolve(null), 250)),
      ])) ?? media[0];

    if (!mostVisibleElement) return this.closeDialog();

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    document.documentElement.classList.remove('zoom-dialog-open');
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Escape closes the dialog; ←/→ step through the images (Jan, 2026-07-03 —
   * keyboard navigation while the zoom is open).
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      // preventDefault so the arrow doesn't also scroll the snap container.
      event.preventDefault();
      this.#selectAdjacentMedia(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Steps the selection to the previous/next image, wrapping at the ends.
   * @param {number} delta - -1 for previous, 1 for next.
   */
  #selectAdjacentMedia(delta) {
    const buttons = Array.from(this.refs.thumbnails?.querySelectorAll('button') ?? []);
    if (!buttons.length) return;

    const current = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const index = (Math.max(current, 0) + delta + buttons.length) % buttons.length;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
